
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

import schoolLogo from "./zpa.png";

/*
|--------------------------------------------------------------------------
| ZION PRAISE ACADEMY CRM SCHOOL SYSTEM
| DAILY PAYMENT TRANSACTION PDF
|--------------------------------------------------------------------------
*/

const SCHOOL_NAME = "Zion Praise Academy";
const SYSTEM_NAME = "CRM School System";

const SCHOOL_ADDRESS =
  "Cowfield 1st Junction, Duport Road, Paynesville, Monrovia, Liberia";

const SCHOOL_CONTACT = "Contact: +231-776-494-010 / +231-880-585-855 / +231-770-540-258";
const SCHOOL_EMAIL = "Email: info@zionpraiseacademy.com";

const COLORS = {
  black: "#09090b",
  deepGray: "#18181b",
  gray: "#6b7280",
  lightGray: "#f3f4f6",
  orange: "#f97316",
  deepOrange: "#ea580c",
  chameleon: "#84cc16",
  white: "#ffffff",
  border: "#d1d5db",
};

/*
|--------------------------------------------------------------------------
| SAFE VALUE HELPERS
|--------------------------------------------------------------------------
*/

// Prevent objects and arrays from being passed directly to PDF <Text>.
const safeText = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map((entry) => safeText(entry, "")).filter(Boolean).join(", ");
  }

  if (typeof value === "object") {
    const displayValue =
      value.full_name ??
      value.fullName ??
      value.name ??
      value.username ??
      value.title ??
      value.label ??
      value.year ??
      value.id;

    return displayValue === null || displayValue === undefined
      ? fallback
      : String(displayValue);
  }

  return fallback;
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const cleaned = String(value).replace(/[$,\s]/g, "");
  const parsed = Number(cleaned);

  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value) =>
  toNumber(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return safeText(value);
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatGeneratedDate = () =>
  new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });

/*
|--------------------------------------------------------------------------
| PDF STYLES
|--------------------------------------------------------------------------
*/

const styles = StyleSheet.create({
  page: {
    paddingTop: 25,
    paddingHorizontal: 25,
    paddingBottom: 48,
    fontSize: 9,
    fontFamily: "Helvetica",
    backgroundColor: COLORS.white,
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#b9c0c8",
    padding: 13,
    borderRadius: 8,
    marginBottom: 13,
  },

  logo: {
    width: 72,
    height: 72,
    objectFit: "contain",
  },

  schoolInformation: {
    flex: 1,
    marginLeft: 14,
    marginRight: 12,
    justifyContent: "center",
  },

  schoolName: {
    fontSize: 19,
    fontWeight: "bold",
    color: COLORS.black,
    marginBottom: 3,
  },

  systemName: {
    fontSize: 11,
    fontWeight: "bold",
    color: COLORS.deepOrange,
    marginBottom: 5,
  },

  schoolAddress: {
    fontSize: 7.5,
    color: COLORS.black,
    marginBottom: 3,
  },

  schoolContact: {
    fontSize: 7.5,
    color: COLORS.black,
    marginBottom: 2,
  },

  reportBadge: {
    width: 125,
    padding: 9,
    backgroundColor: COLORS.deepOrange,
    borderRadius: 7,
    alignItems: "center",
    justifyContent: "center",
  },

  badgeTitle: {
    color: COLORS.white,
    fontSize: 9,
    fontWeight: "bold",
    marginBottom: 5,
    textAlign: "center",
  },

  badgeText: {
    color: COLORS.white,
    fontSize: 7.5,
    textAlign: "center",
  },

  infoBox: {
    backgroundColor: COLORS.lightGray,
    padding: 10,
    borderRadius: 6,
    marginBottom: 12,
  },

  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },

  infoRowLast: {
    marginBottom: 0,
  },

  infoLabel: {
    width: "40%",
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.deepGray,
  },

  infoValue: {
    width: "60%",
    fontSize: 8,
    color: COLORS.deepGray,
    textAlign: "right",
  },

  summaryContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 13,
  },

  summaryCard: {
    width: "32%",
    padding: 10,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 6,
    backgroundColor: COLORS.white,
  },

  summaryTitle: {
    fontSize: 8,
    color: COLORS.gray,
  },

  summaryValue: {
    marginTop: 5,
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.deepOrange,
  },

  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: COLORS.border,
  },

  tableHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.black,
    paddingVertical: 7,
  },

  tableRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderBottomWidth: 1,
    borderColor: COLORS.border,
    paddingVertical: 6,
    minHeight: 24,
  },

  alternateRow: {
    backgroundColor: "#fafafa",
  },

  cell: {
    paddingHorizontal: 2,
    fontSize: 6.5,
    color: COLORS.deepGray,
  },

  headerCell: {
    paddingHorizontal: 2,
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.white,
  },

  // Column widths total 100%.
  numberCell: {
    width: "5%",
    textAlign: "center",
  },

  idCell: {
    width: "6%",
    textAlign: "center",
  },

  studentCell: {
    width: "16%",
  },

  feeCell: {
    width: "13%",
  },

  categoryCell: {
    width: "10%",
  },

  amountCell: {
    width: "11%",
    textAlign: "right",
  },

  balanceCell: {
    width: "11%",
    textAlign: "right",
  },

  payerCell: {
    width: "10%",
  },

  createdCell: {
    width: "9%",
  },

  dateCell: {
    width: "9%",
  },

  emptyCell: {
    width: "100%",
    padding: 12,
    textAlign: "center",
    fontSize: 8,
    color: COLORS.gray,
  },

  footer: {
    position: "absolute",
    bottom: 17,
    left: 25,
    right: 25,
    textAlign: "center",
    fontSize: 7,
    color: COLORS.gray,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    paddingTop: 6,
  },
});

/*
|--------------------------------------------------------------------------
| DAILY PAYMENT TRANSACTION PDF COMPONENT
|--------------------------------------------------------------------------
*/

const DailyPaymentTransactionPDF = ({
  transactions = [],
  title = "Daily Transaction Record",
  studentReport = false,
  studentName = "",
}) => {
  const safeTransactions = Array.isArray(transactions)
    ? transactions
    : [];

  const totalPaid = safeTransactions.reduce(
    (sum, item) => sum + toNumber(item?.amount_paid),
    0
  );

  const totalBalance = safeTransactions.reduce(
    (sum, item) => sum + toNumber(item?.balance),
    0
  );

  const reportTitle = studentReport
    ? `${safeText(studentName, "Student")} Payment History`
    : safeText(title, "Daily Transaction Record");

  return (
    <Document
      title={`${SCHOOL_NAME} ${SYSTEM_NAME} - Payment Report`}
      author={`${SCHOOL_NAME} ${SYSTEM_NAME}`}
      subject="Daily Payment Transaction Record"
    >
      <Page
        size="A4"
        orientation="landscape"
        style={styles.page}
        wrap
      >
        {/* SCHOOL HEADER */}

        <View style={styles.header}>
          <Image
            src={schoolLogo}
            style={styles.logo}
          />

          <View style={styles.schoolInformation}>
            <Text style={styles.schoolName}>
              {SCHOOL_NAME}
            </Text>

            <Text style={styles.systemName}>
              {SYSTEM_NAME}
            </Text>

            <Text style={styles.schoolAddress}>
              {SCHOOL_ADDRESS}
            </Text>

            <Text style={styles.schoolContact}>
              {SCHOOL_CONTACT}
            </Text>

            <Text style={styles.schoolContact}>
              {SCHOOL_EMAIL}
            </Text>
          </View>

          <View style={styles.reportBadge}>
            <Text style={styles.badgeTitle}>
              PAYMENT REPORT
            </Text>

            <Text style={styles.badgeText}>
              {reportTitle}
            </Text>
          </View>
        </View>

        {/* REPORT INFORMATION */}

        <View style={styles.infoBox}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>
              Report Type:
            </Text>

            <Text style={styles.infoValue}>
              {studentReport
                ? "Individual Student Payment Record"
                : "Daily Transaction Record"}
            </Text>
          </View>

          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>
              Generated Date:
            </Text>

            <Text style={styles.infoValue}>
              {formatGeneratedDate()}
            </Text>
          </View>

          {studentReport && (
            <View style={[styles.infoRow, styles.infoRowLast]}>
              <Text style={styles.infoLabel}>
                Student:
              </Text>

              <Text style={styles.infoValue}>
                {safeText(studentName)}
              </Text>
            </View>
          )}
        </View>

        {/* SUMMARY CARDS */}

        <View style={styles.summaryContainer}>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryTitle}>
              Total Transactions
            </Text>

            <Text style={styles.summaryValue}>
              {safeTransactions.length.toLocaleString("en-US")}
            </Text>
          </View>

          <View style={styles.summaryCard}>
            <Text style={styles.summaryTitle}>
              Total Paid
            </Text>

            <Text style={styles.summaryValue}>
              ${formatMoney(totalPaid)}
            </Text>
          </View>

          <View style={styles.summaryCard}>
            <Text style={styles.summaryTitle}>
              Remaining Balance
            </Text>

            <Text style={styles.summaryValue}>
              ${formatMoney(totalBalance)}
            </Text>
          </View>
        </View>

        {/* TRANSACTION TABLE */}

        <View style={styles.table}>
          <View style={styles.tableHeader} fixed>
            <Text style={[styles.headerCell, styles.numberCell]}>
              No.
            </Text>

            <Text style={[styles.headerCell, styles.idCell]}>
              ID
            </Text>

            <Text style={[styles.headerCell, styles.studentCell]}>
              Student
            </Text>

            <Text style={[styles.headerCell, styles.feeCell]}>
              Fee Type
            </Text>

            <Text style={[styles.headerCell, styles.categoryCell]}>
              Category
            </Text>

            <Text style={[styles.headerCell, styles.amountCell]}>
              Paid ($)
            </Text>

            <Text style={[styles.headerCell, styles.balanceCell]}>
              Balance ($)
            </Text>

            <Text style={[styles.headerCell, styles.payerCell]}>
              Payer
            </Text>

            <Text style={[styles.headerCell, styles.createdCell]}>
              Created By
            </Text>

            <Text style={[styles.headerCell, styles.dateCell]}>
              Date
            </Text>
          </View>

          {safeTransactions.length > 0 ? (
            safeTransactions.map((item, index) => (
              <View
                key={String(item?.id ?? `transaction-${index}`)}
                style={[
                  styles.tableRow,
                  index % 2 === 1 ? styles.alternateRow : {},
                ]}
                wrap={false}
              >
                <Text style={[styles.cell, styles.numberCell]}>
                  {index + 1}
                </Text>

                <Text style={[styles.cell, styles.idCell]}>
                  {safeText(item?.id)}
                </Text>

                <Text style={[styles.cell, styles.studentCell]}>
                  {safeText(item?.student_name)}
                </Text>

                <Text style={[styles.cell, styles.feeCell]}>
                  {safeText(item?.fee_type)}
                </Text>

                <Text style={[styles.cell, styles.categoryCell]}>
                  {safeText(item?.payment_category)}
                </Text>

                <Text style={[styles.cell, styles.amountCell]}>
                  {formatMoney(item?.amount_paid)}
                </Text>

                <Text style={[styles.cell, styles.balanceCell]}>
                  {formatMoney(item?.balance)}
                </Text>

                <Text style={[styles.cell, styles.payerCell]}>
                  {safeText(item?.payer_name)}
                </Text>

                <Text style={[styles.cell, styles.createdCell]}>
                  {safeText(item?.created_by)}
                </Text>

                <Text style={[styles.cell, styles.dateCell]}>
                  {formatDate(item?.created_at)}
                </Text>
              </View>
            ))
          ) : (
            <View style={styles.tableRow}>
              <Text style={styles.emptyCell}>
                No payment transactions available for this report.
              </Text>
            </View>
          )}
        </View>

        {/* FOOTER */}

        <Text style={styles.footer} fixed>
          {SCHOOL_NAME} {SYSTEM_NAME} | Official Payment Transaction
          Record | Generated {formatGeneratedDate()}
        </Text>
      </Page>
    </Document>
  );
};

export { DailyPaymentTransactionPDF };
export default DailyPaymentTransactionPDF;
